'use client';

import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

// Cadence matches the original pre-ticket typewriter, so this reads as the same
// animation continuing rather than a different effect taking over.
export const TYPE_TICK_MS = 40;
// Only the first character after a slide uses this much shorter delay, so the
// chip doesn't sit blank for a full tick before typing appears to start — every
// character after that (including the whole first question on load) still
// types at the normal TYPE_TICK_MS cadence.
export const START_TICK_MS = 0;
const HOLD_MS = 2200;

export const SLIDE_MS = 280;

type Phase = 'typing' | 'holding' | 'sliding';

// Index advances in the same commit the slide ends, so the baseline reset lines
// up with the already-rotated content and reads as continuous, not a snap-back
// (matches Carvana.Composer.UI's src/hooks/useTopicRotation.ts).
const delayMsByPhase = (typeTickMs: number): Record<Phase, number> => ({
  holding: HOLD_MS,
  sliding: SLIDE_MS,
  typing: typeTickMs
});

type RotationState = {
  currentQuestion: string;
  displayedText: string;
  previousQuestion: string;
  sliding: boolean;
  typing: boolean;
};

// `questions` must be a stable reference (e.g. a module-level constant) — it's an
// effect dependency below, so a new array identity on every render would reset
// the timer each time and the animation would never advance.
export default function useComposerStarterQuestionRotation(
  questions: ReadonlyArray<string>,
  enabled = true
): RotationState {
  const prefersReducedMotion = useReducedMotion();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [phase, setPhase] = useState<Phase>('typing');
  const [hasSlidOnce, setHasSlidOnce] = useState(false);
  const animating = enabled && !prefersReducedMotion;
  const targetQuestion = questions[questionIndex];
  const canSlide = questions.length > 1;

  useEffect(() => {
    if (!animating) return undefined;

    // Typing-complete is a state transition, not a timed step — advance to
    // holding immediately rather than waiting out another tick first.
    if (phase === 'typing' && charCount >= targetQuestion.length) {
      setPhase('holding');

      return undefined;
    }

    // With nothing to rotate to, hold the fully-typed question forever.
    if (phase === 'holding' && !canSlide) return undefined;

    const isFirstCharAfterSlide =
      hasSlidOnce && phase === 'typing' && charCount === 0;
    const delayMs = delayMsByPhase(
      isFirstCharAfterSlide ? START_TICK_MS : TYPE_TICK_MS
    )[phase];

    const timeoutId = setTimeout(() => {
      if (phase === 'typing') {
        setCharCount(previous => previous + 1);
      } else if (phase === 'holding') {
        setPhase('sliding');
      } else {
        setPhase('typing');
        setCharCount(0);
        setHasSlidOnce(true);
        setQuestionIndex(previous => (previous + 1) % questions.length);
      }
    }, delayMs);

    return () => clearTimeout(timeoutId);
  }, [
    animating,
    canSlide,
    charCount,
    hasSlidOnce,
    phase,
    questions.length,
    targetQuestion
  ]);

  const { length } = questions;
  const typedText =
    prefersReducedMotion || phase !== 'typing'
      ? targetQuestion
      : targetQuestion.slice(0, charCount);

  return {
    currentQuestion: targetQuestion,
    // A non-breaking space, not '', keeps the chip's line box (and measured
    // height) from collapsing before the first character types in.
    displayedText: typedText || ' ',
    previousQuestion: questions[(questionIndex - 1 + length) % length],
    sliding: phase === 'sliding',
    typing: animating && phase === 'typing'
  };
}
